import { Link, Navigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import Reveal from "../components/Reveal";
import VideoCard from "../components/VideoCard";
import QuoteForm from "../components/QuoteForm";
import ClientMarquee from "../components/ClientMarquee";
import SEO from "../components/SEO";
import { serviceJsonLd } from "../data/seo";
import { TESTIMONIALS } from "../data/content";
import { getIndustry, relatedVideosFor, INDUSTRIES } from "../data/industries";

export default function IndustryPage({ slug }) {
  const industry = getIndustry(slug);

  if (!industry) return <Navigate to="/" replace />;

  const videos = relatedVideosFor(industry);

  return (
    <main className="pt-28 pb-12" data-testid={`industry-page-${industry.slug}`}>
      <SEO
        title={industry.title}
        description={industry.description}
        path={`/${industry.slug}`}
        keywords={industry.keywords}
        jsonLd={serviceJsonLd({
          name: `${industry.industry} Video Production Liverpool`,
          description: industry.description,
          path: `/${industry.slug}`,
        })}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Video Production", path: "/video-production-in-liverpool" },
          { name: industry.industry, path: `/${industry.slug}` },
        ]}
      />

      {/* Hero */}
      <section className="yv-container">
        <Reveal>
          <p className="yv-eyebrow">{industry.keyword}</p>
          <h1 className="yv-h1 mt-3 max-w-5xl">{industry.h1}</h1>
          <p className="text-white/70 max-w-3xl mt-6 text-lg leading-relaxed">{industry.intro}</p>
          <div className="flex gap-3 mt-8">
            <Link to="/contact" className="yv-btn yv-btn-primary" data-testid="industry-cta-quote">Get a quote</Link>
            <Link to="/showreel" className="yv-btn yv-btn-secondary">See the reel</Link>
          </div>
        </Reveal>
      </section>

      <ClientMarquee />

      {/* What's included */}
      <section className="yv-container mt-16 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <p className="yv-eyebrow">What you get</p>
          <h2 className="yv-h2 text-white mt-2 mb-6">
            {industry.industry} video production, built for the brief.
          </h2>
          <ul className="space-y-3">
            {industry.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-white/80">
                <CheckCircle2 className="text-[var(--yellow)] mt-0.5 flex-shrink-0" size={20} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={120}>
          <QuoteForm heading="Get a quote within 4 hours" />
        </Reveal>
      </section>

      {/* Related work */}
      {videos.length > 0 && (
        <section className="yv-container mt-20">
          <Reveal>
            <p className="yv-eyebrow">Recent work</p>
            <h2 className="yv-h2 text-white mt-2 mb-10">
              Films we've made for {industry.industry.toLowerCase()} and similar brands.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {videos.map((v) => (
              <article key={v.id} className="bg-white/[0.02] border border-white/10 rounded-md overflow-hidden">
                <VideoCard video={v} />
                <div className="p-6">
                  <p className="yv-eyebrow text-[10px] mb-2">{v.client}</p>
                  <h3 className="yv-h3 text-white">{v.title}</h3>
                  <p className="text-white/65 text-sm mt-3 leading-relaxed">{v.summary}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="yv-container mt-20">
        <Reveal>
          <p className="yv-eyebrow">Testimonials</p>
          <h2 className="yv-h2 text-white mt-2 mb-10">What our clients say.</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <blockquote key={i} className="p-6 bg-white/[0.03] border border-white/10 rounded-md">
              <p className="yv-quote">"{t.quote}"</p>
              <footer className="mt-5">
                <p className="text-white font-bold text-sm">{t.author}</p>
                <p className="text-white/50 text-xs">{t.company}</p>
              </footer>
            </blockquote>
          ))}
        </div>
      </section>

      {/* Related industries */}
      <section className="yv-container mt-20">
        <Reveal>
          <p className="yv-eyebrow">Other industries we work with</p>
          <h2 className="yv-h2 text-white mt-2 mb-10">Specialist video production for…</h2>
        </Reveal>
        <div className="flex flex-wrap gap-2">
          {INDUSTRIES.filter((i) => i.slug !== industry.slug).map((i) => (
            <Link
              key={i.slug}
              to={`/${i.slug}`}
              className="px-4 py-2 bg-white/[0.04] border border-white/10 rounded-full text-sm text-white/75 hover:text-[var(--yellow)] hover:border-[var(--yellow)] transition"
            >
              {i.industry}
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
